import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../Api";
import Popup from "../components/PopUP";

export default function AirportUpdate() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID from URL (e.g., /airport/update/:id)
  const isEditMode = Boolean(id);

  // Loading state for GET API
  const [isFetching, setIsFetching] = useState(false);

  // Exact API payload structure
  const initialFormData = {
    general_information: { name: "", type: "", url: "" },
    location: { lat: "", lon: "", city: "", state: "", country: "" },
    time_information: { tz: "", utc: "" },
    codes: { code: "", icao: "" },
    runway_information: {
      number_Runways: "",
      runway_s_direction: "",
      runway_s_length_m: "",
      elevation_ft: "",
      runway_surface_type: "",
      number__terminals: "",
    },
    operational_statistics: {
      annual_movements_approx: "",
      annual_passenger_traffic_approx: "",
    },
    id: "", 
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (message, type) => {
    setToast({ message, type });
  };

  // 1. FETCH DETAILS ON MOUNT (If ID exists)
  useEffect(() => {
    if (isEditMode) {
      const fetchAirportDetails = async () => {
        setIsFetching(true);
        try {
          const response = await api.get(`/airplane-service/airport/${id}`);
          const data = response.data.data;

          setFormData({
            id: data.id || id,
            general_information: data.general_information || {
              name: data.name || "",
              type: data.type || "",
              url: data.website_url || "",
            },
            location: data.location || {
              lat: data.latitude_deg || "",
              lon: data.longitude_deg || "",
              city: data.city || "",
              state: data.state || "",
              country: data.country || "",
            },
            time_information: data.time_information || {
              tz: data.timezone || "",
              utc: data.utc_offset || "",
            },
            codes: data.codes || {
              code: data.iata_code || "",
              icao: data.icao || "",
            },
            runway_information: data.runway_information || {
              number_Runways: data.number_of_runways || "",
              runway_s_direction: data.runway_direction || "",
              runway_s_length_m: data.runway_length || "",
              elevation_ft: data.elev || "",
              runway_surface_type: data.runway_surface_type || "",
              number__terminals: data.number_of_terminals || "",
            },
            operational_statistics: data.operational_statistics || {
              annual_movements_approx: data.annual_movements || "",
              annual_passenger_traffic_approx: data.annual_passenger_traffic || "",
            },
          });
        } catch (error) {
          console.error("Fetch Error:", error);
          showToast("Failed to fetch airport details", "danger");
        } finally {
          setIsFetching(false);
        }
      };

      fetchAirportDetails();
    }
  }, [id, isEditMode]);

  // Generic handler for nested state updates
  const handleNestedChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.general_information.name.trim()) newErrors.name = "Airport name is required";
    if (!formData.general_information.type.trim()) newErrors.type = "Airport type is required";
    if (!formData.location.city.trim()) newErrors.city = "City is required";
    if (!formData.location.country.trim()) newErrors.country = "Country is required";
    if (!formData.codes.code.trim()) newErrors.code = "IATA Code is required";
    if (!formData.codes.icao.trim()) newErrors.icao = "ICAO Code is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Add ID to payload for update
        const payload = { ...formData, id: id || formData.id };

        const response = isEditMode
          ? await api.patch('/airplane-service/airport/admin', payload)
          : await api.post('/airplane-service/airport/admin', payload);

        showToast(response.data.detail || "Airport saved successfully", 'success');
        
        if (!isEditMode) {
            setFormData(initialFormData); // Reset only if it's a new addition
        }

      } catch (error) {
        console.error("API Error:", error);
        const errorMsg = error.response?.data?.detail || "Failed to submit form. Please try again.";
        showToast(errorMsg, 'danger');
      }
    } else {
      showToast('Please fill all required fields correctly.', 'danger');
    }
  };

  return (
    <>
      <Sidebar />
      <div className="main-wrapper">
        <Header />

        <Popup
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
          duration={3000}
        />

        <div className="content-wrapper">
          <section className="main-section spacer-y">
            <div className="container">
              <div className="row gy-4">
                <div className="col-xl-12">
                  
                  {/* Top Banner */}
                  <div className="common_main_bg_wrapper">
                    <div className="common_main_heading_wrapper">
                      <div className="vstack">
                        <nav className="breadcrumb_nav">
                          <ul className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                              <Link to="/dashboard">Dashboard</Link>
                            </li>
                            <li className="breadcrumb-item">
                              <Link to="/airport">Airport Management</Link>
                            </li>
                            <li className="breadcrumb-item text-capitalize">
                              {isEditMode ? "Update Airport" : "Add New Airport"}
                            </li>
                          </ul>
                        </nav>
                        <h1>Airport Management</h1>
                      </div>
                      <div className="d-flex flex-end">
                        <Link to="/airport" className="secondary_btn">
                          Back
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Show Loader while Fetching Details */}
                  {isFetching ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading Details...</span>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="common-outer-card mt-4">
                        <div className="common_sub_heading_wrapper">
                          <h2>{isEditMode ? "Update Airport Details" : "Add New Airport"}</h2>
                        </div>
                        
                        <div className="common_content_blk">
                          
                          {/* 1. GENERAL INFORMATION */}
                          <h5 className="mb-3 ">General Information</h5>
                          <div className="row g-4 mb-4">
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Airport Name <span className="text-danger">*</span></label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. Choiseul Bay Airport"
                                    value={formData.general_information.name}
                                    onChange={(e) => handleNestedChange("general_information", "name", e.target.value)}
                                  />
                                  <span className="error">{errors.name}</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Airport Type <span className="text-danger">*</span></label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. Civil Airport"
                                    value={formData.general_information.type}
                                    onChange={(e) => handleNestedChange("general_information", "type", e.target.value)}
                                  />
                                  <span className="error">{errors.type}</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Website URL</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter Website URL"
                                    value={formData.general_information.url}
                                    onChange={(e) => handleNestedChange("general_information", "url", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 2. LOCATION DETAILS */}
                          <h5 className="mb-3">Location Details</h5>
                          <div className="row g-4 mb-4">
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>City <span className="text-danger">*</span></label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="City"
                                    value={formData.location.city}
                                    onChange={(e) => handleNestedChange("location", "city", e.target.value)}
                                  />
                                  <span className="error">{errors.city}</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>State / Province</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="State"
                                    value={formData.location.state}
                                    onChange={(e) => handleNestedChange("location", "state", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Country <span className="text-danger">*</span></label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Country"
                                    value={formData.location.country}
                                    onChange={(e) => handleNestedChange("location", "country", e.target.value)}
                                  />
                                  <span className="error">{errors.country}</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="form_field">
                                <label>Latitude (deg)</label>
                                <div className="input_field">
                                  <input
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    placeholder="e.g. -6.711944"
                                    value={formData.location.lat}
                                    onChange={(e) => handleNestedChange("location", "lat", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="form_field">
                                <label>Longitude (deg)</label>
                                <div className="input_field">
                                  <input
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    placeholder="e.g. 156.396111"
                                    value={formData.location.lon}
                                    onChange={(e) => handleNestedChange("location", "lon", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 3. CODES & TIME */}
                          <h5 className="mb-3">Codes & Time Information</h5>
                          <div className="row g-4 mb-4">
                            <div className="col-12 col-md-3">
                              <div className="form_field">
                                <label>IATA Code <span className="text-danger">*</span></label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. CHY"
                                    value={formData.codes.code}
                                    onChange={(e) => handleNestedChange("codes", "code", e.target.value)}
                                  />
                                  <span className="error">{errors.code}</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="form_field">
                                <label>ICAO Code <span className="text-danger">*</span></label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. AGGC"
                                    value={formData.codes.icao}
                                    onChange={(e) => handleNestedChange("codes", "icao", e.target.value)}
                                  />
                                  <span className="error">{errors.icao}</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="form_field">
                                <label>Timezone (TZ)</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. Pacific/Guadalcanal"
                                    value={formData.time_information.tz}
                                    onChange={(e) => handleNestedChange("time_information", "tz", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="form_field">
                                <label>UTC Offset</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. +11"
                                    value={formData.time_information.utc}
                                    onChange={(e) => handleNestedChange("time_information", "utc", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 4. RUNWAY INFORMATION */}
                          <h5 className="mb-3">Runway Information</h5>
                          <div className="row g-4 mb-4">
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Number of Runways</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. 1"
                                    value={formData.runway_information.number_Runways}
                                    onChange={(e) => handleNestedChange("runway_information", "number_Runways", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Runway Direction</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. 13/31"
                                    value={formData.runway_information.runway_s_direction}
                                    onChange={(e) => handleNestedChange("runway_information", "runway_s_direction", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Runway Length (m)</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. 660.0"
                                    value={formData.runway_information.runway_s_length_m}
                                    onChange={(e) => handleNestedChange("runway_information", "runway_s_length_m", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Elevation (ft)</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. Not Specified"
                                    value={formData.runway_information.elevation_ft}
                                    onChange={(e) => handleNestedChange("runway_information", "elevation_ft", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Runway Surface Type</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. Grass, Concrete"
                                    value={formData.runway_information.runway_surface_type}
                                    onChange={(e) => handleNestedChange("runway_information", "runway_surface_type", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form_field">
                                <label>Number of Terminals</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. Not Specified"
                                    value={formData.runway_information.number__terminals}
                                    onChange={(e) => handleNestedChange("runway_information", "number__terminals", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 5. OPERATIONAL STATISTICS */}
                          <h5 className="mb-3">Operational Statistics</h5>
                          <div className="row g-4 mb-4">
                            <div className="col-12 col-md-6">
                              <div className="form_field">
                                <label>Annual Movements (Approx)</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. Not Specified"
                                    value={formData.operational_statistics.annual_movements_approx}
                                    onChange={(e) => handleNestedChange("operational_statistics", "annual_movements_approx", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="form_field">
                                <label>Annual Passenger Traffic (Approx)</label>
                                <div className="input_field">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. Not Specified"
                                    value={formData.operational_statistics.annual_passenger_traffic_approx}
                                    onChange={(e) => handleNestedChange("operational_statistics", "annual_passenger_traffic_approx", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Form Actions */}
                          <div className="main_action_blk hstack gap-3 justify-content-start mt-4">
                            <button 
                              type="button" 
                              className="bg_primary_outline_btn rounded-pill"
                              onClick={() => navigate('/airport')}
                            >
                              Cancel
                            </button>
                            <button type="submit" className="primary-btn rounded-pill">
                              {isEditMode ? "Update Airport" : "Save Airport"}
                            </button>
                          </div>

                        </div>
                      </div>
                    </form>
                  )}

                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}